<script lang="ts">
  /**
   * Quilty Platform Demo - Interactive slideshow presentation
   * Showcasing the power of Hollywood's intelligence infrastructure
   */
  import { MetaTags } from 'svelte-meta-tags';
  import JsonLd from '$lib/components/seo/JsonLd.svelte';
  import type { WithContext, SoftwareApplication } from 'schema-dts';
  import emblaCarouselSvelte from 'embla-carousel-svelte';
  import Autoplay from 'embla-carousel-autoplay';
  import { Button } from '$lib/components/ui/button';
  import { Badge } from '$lib/components/ui/badge';
  // @ts-ignore - @lucide/svelte types
  import {
    Play,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    TrendingUp,
    Users,
    Globe,
    Zap,
  } from '@lucide/svelte';

  // Carousel state
  let emblaApi: any = $state(null);
  let selectedIndex = $state(0);
  let scrollSnaps: number[] = $state([]);
  let canScrollPrev = $state(false);
  let canScrollNext = $state(false);

  // Autoplay plugin
  const autoplayOptions = {
    delay: 8000,
    stopOnInteraction: true,
  };

  function onInit(event: CustomEvent) {
    emblaApi = event.detail;
    scrollSnaps = emblaApi.scrollSnapList();

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();
  }

  function onSelect() {
    if (!emblaApi) return;
    selectedIndex = emblaApi.selectedScrollSnap();
    canScrollPrev = emblaApi.canScrollPrev();
    canScrollNext = emblaApi.canScrollNext();
  }

  function scrollPrev() {
    emblaApi?.scrollPrev();
  }

  function scrollNext() {
    emblaApi?.scrollNext();
  }

  function scrollTo(index: number) {
    emblaApi?.scrollTo(index);
  }

  // Slide content based on investor demo script
  const slides = [
    {
      id: 'hook',
      title: 'The Hook: Preview Analysis',
      subtitle: 'Immediate value that creates upgrade demand',
      icon: Sparkles,
      description:
        'In seconds, users get a foundational look at their project: an initial Quilty Score, AI-generated logline, genre classification, and commercial viability assessment. This free entry point tells you *what* the potential issues are—but not *how* to fix them.',
      example: 'Knives Out (2019)',
      features: [
        'Instant Quilty Score (0-100)',
        'AI-generated logline and synopsis',
        'Genre classification and market fit',
        'High-level commercial viability',
      ],
      image: '/demo/preview-analysis.svg',
      color: 'quiltyTeal',
    },
    {
      id: 'financiers',
      title: 'For Financiers: The Commercial Case',
      subtitle: 'Greenlight Financial Suite & Comps Engine',
      icon: TrendingUp,
      description:
        'Instead of a single speculative box office number, Quilty provides a viability forecast with relevant, modern comparisons. Our Comps Engine identifies films like Glass Onion (2022), Murder on the Orient Express (2017), and Game Night (2018)—not outdated examples from the 80s.',
      example: 'Knives Out (2019) Analysis',
      features: [
        'Multi-scenario ROI projections',
        'AI-powered comparable film analysis',
        'Budget-to-revenue forecasting',
        'Risk assessment and mitigation strategies',
      ],
      image: '/demo/financial-suite.svg',
      color: 'quiltyRed',
    },
    {
      id: 'writers',
      title: 'For Writers: Creative Intelligence',
      subtitle: 'Narrative Genome & Title/Synopsis Lab',
      icon: Sparkles,
      description:
        'Our Narrative Genome visually maps the story\'s DNA. The Emotional Arc shows classic structures like "Man in a Hole"—a fall and rise that\'s incredibly satisfying for audiences. The Character Network confirms tightly woven plots centered around key protagonists.',
      example: 'Story Structure Analysis',
      features: [
        'Visual emotional arc mapping',
        'Character relationship networks',
        'Pacing and tension analysis',
        'Market-ready title and synopsis generation',
      ],
      image: '/demo/narrative-genome.svg',
      color: 'quiltyTeal',
    },
    {
      id: 'producers',
      title: 'For Producers: Production Blueprint',
      subtitle: 'Automated script breakdown and budgeting',
      icon: Users,
      description:
        'Our AI automatically generates a preliminary script breakdown, tagging and counting all elements—locations, cast, props, VFX—that drive the budget. This saves days of manual work and provides an immediate check on production complexity.',
      example: 'Production Planning',
      features: [
        'Automated element tagging',
        'Location and cast counting',
        'VFX complexity assessment',
        'Budget driver identification',
      ],
      image: '/demo/production-breakdown.svg',
      color: 'quiltyRed',
    },
    {
      id: 'global',
      title: 'Global Strategy: Market Intelligence',
      subtitle: 'Fatherland (1994) - Navigating complex international projects',
      icon: Globe,
      description:
        'A human reader might see a "German story" and stop there. Our AI understands nuances, identifying three primary markets: Germany (high resonance, cultural sensitivities), UK (British author, historical interest), and US (thriller genre popularity). Multi-territory co-production strategy in seconds.',
      example: 'Fatherland (1994) Analysis',
      features: [
        'Global market resonance mapping',
        'Cultural sensitivity analysis',
        'Co-production opportunity identification',
        'Territory-specific distribution strategies',
      ],
      image: '/demo/global-resonance.svg',
      color: 'quiltyTeal',
    },
    {
      id: 'action',
      title: 'From Insight to Action',
      subtitle: 'TheBiz Contact Matching',
      icon: Zap,
      description:
        'Based on the analysis, our system queries our industry database to generate a targeted call list: producers who have successfully adapted similar work, and distributors who specialize in your genre. From complex script to actionable strategy in under an hour.',
      example: 'Industry Network Access',
      features: [
        'Industry contact database',
        'AI-powered matchmaking',
        'Project-specific recommendations',
        'Company profiles and details',
      ],
      image: '/demo/thebiz-matching.svg',
      color: 'quiltyRed',
    },
  ];

  const demoSchema: WithContext<SoftwareApplication> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Quilty',
    description: 'AI-powered screenplay analysis platform for writers, producers, and executives. Instant script coverage with market intelligence, talent recommendations, and financial forecasting.',
    url: 'https://quilty.app',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free preview analysis available',
    },
    featureList: [
      'AI-Powered Script Coverage',
      'Market Intelligence & Box Office Forecasting',
      'Talent Recommendations',
      'Budget Forecasting',
      'Production Breakdown',
      'Global Market Strategy',
    ],
  };
</script>

<MetaTags
  title="Quilty Platform Demo — See AI Script Coverage in Action"
  description="Experience how Quilty transforms screenplay analysis with AI-powered intelligence. From preview analysis to global market strategy, see the complete platform workflow."
  openGraph={{
    title: 'Quilty Platform Demo — AI Script Coverage in Action',
    description:
      'Interactive demo showcasing AI-powered screenplay analysis, financial forecasting, narrative intelligence, and global market strategy.',
    url: 'https://quilty.app/demo',
    siteName: 'Quilty',
    type: 'website',
    images: [
      {
        url: 'https://quilty.app/images/og-home.jpg',
        width: 1200,
        height: 630,
        alt: 'Quilty Platform Demo',
      },
    ],
  }}
  twitter={{
    cardType: 'summary_large_image',
    title: 'Quilty Platform Demo — AI Script Coverage in Action',
    description: 'See how Quilty transforms screenplay analysis with AI-powered intelligence.',
    site: '@quiltyapp',
  }}
/>

<JsonLd schema={demoSchema} />

<div class="min-h-screen bg-background">
  <!-- Hero Section -->
  <section class="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-quiltyNavy dark">
    <!-- Animated background -->
    <div
      class="absolute inset-0 bg-linear-to-br from-quiltyNavy via-quiltyNavy to-quiltyRed/30"
    ></div>

    <div class="relative container mx-auto max-w-6xl text-center">
      <Badge
        variant="secondary"
        class="mb-6 animate-pulse shadow-lg shadow-quiltyTeal/20 uppercase tracking-[0.3em] text-xs"
      >
        <Play class="w-3 h-3 mr-2 inline" />
        Interactive Platform Demo
      </Badge>

      <h1
        class="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-serif"
      >
        See Hollywood's Intelligence Infrastructure in Action
      </h1>

      <p class="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
        Follow the journey from screenplay upload to actionable market strategy. Discover how Quilty
        transforms $300B of gut-driven decisions into data-driven intelligence.
      </p>

      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <Button size="lg" href="#demo-slides" class="group">
          <Play class="w-4 h-4 mr-2 group-hover:animate-pulse" />
          Start Demo
        </Button>
        <Button
          size="lg"
          variant="outline"
          href="mailto:demo@quilty.app"
          class="border-white/20 text-white hover:bg-white/10"
        >
          Schedule Live Demo
        </Button>
      </div>
    </div>
  </section>

  <!-- Carousel Section -->
  <section
    id="demo-slides"
    class="py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-background to-gainsboro/30"
  >
    <div class="container mx-auto max-w-7xl">
      <!-- Carousel Container -->
      <div class="relative">
        <div
          class="overflow-hidden rounded-2xl shadow-2xl"
          use:emblaCarouselSvelte={{
            options: { loop: true },
            plugins: [Autoplay(autoplayOptions)],
          }}
          onemblaInit={onInit}
        >
          <div class="flex">
            {#each slides as slide (slide.id)}
              {@const Icon = slide.icon}
              <div class="flex-[0_0_100%] min-w-0">
                <div class="bg-white border-2 border-quiltyNavy/10 p-8 md:p-12 lg:p-16">
                  <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    <!-- Content -->
                    <div class="space-y-6">
                      <div class="flex items-center gap-3">
                        <div class="p-3 rounded-xl bg-{slide.color}/20">
                          <Icon class="w-6 h-6 text-{slide.color}" />
                        </div>
                        <Badge variant="outline" class="text-xs uppercase tracking-wider">
                          {slide.example}
                        </Badge>
                      </div>

                      <div>
                        <h2 class="text-3xl md:text-4xl font-bold text-quiltyNavy mb-3 font-serif">
                          {slide.title}
                        </h2>
                        <p class="text-lg text-quiltyRed font-medium mb-4">
                          {slide.subtitle}
                        </p>
                        <p class="text-quiltyNavy/80 leading-relaxed">
                          {slide.description}
                        </p>
                      </div>

                      <div class="space-y-2">
                        <p class="text-sm font-semibold text-quiltyNavy/60 uppercase tracking-wide">
                          Key Features:
                        </p>
                        <ul class="space-y-2">
                          {#each slide.features as feature}
                            <li class="flex items-start gap-2 text-quiltyNavy/70">
                              <span class="text-{slide.color} mt-1">✓</span>
                              <span>{feature}</span>
                            </li>
                          {/each}
                        </ul>
                      </div>
                    </div>

                    <!-- Screenshot -->
                    <div
                      class="relative aspect-video bg-linear-to-br from-gainsboro/50 to-quiltyNavy/5 rounded-xl border-2 border-quiltyNavy/10 overflow-hidden"
                    >
                      <img src={slide.image} alt={slide.title} class="w-full h-full object-cover" />
                    </div>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Navigation Buttons -->
        <button
          onclick={scrollPrev}
          disabled={!canScrollPrev}
          class="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all min-h-11 min-w-11"
          aria-label="Previous slide"
        >
          <ChevronLeft class="w-6 h-6 text-quiltyNavy" />
        </button>

        <button
          onclick={scrollNext}
          disabled={!canScrollNext}
          class="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 shadow-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-all min-h-11 min-w-11"
          aria-label="Next slide"
        >
          <ChevronRight class="w-6 h-6 text-quiltyNavy" />
        </button>

        <!-- Dots Navigation -->
        <div class="flex justify-center gap-2 mt-8">
          {#each scrollSnaps as _, index}
            <button
              onclick={() => scrollTo(index)}
              class="w-3 h-3 rounded-full transition-all {selectedIndex === index
                ? 'bg-quiltyTeal w-8'
                : 'bg-quiltyNavy/20 hover:bg-quiltyNavy/40'}"
              aria-label="Go to slide {index + 1}"
            ></button>
          {/each}
        </div>
      </div>

      <!-- Stats Section -->
      <div class="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="bg-white rounded-xl p-6 border-2 border-quiltyNavy/10 text-center">
          <p class="text-4xl font-bold text-quiltyTeal mb-2">AI</p>
          <p class="text-sm text-quiltyNavy/70">Budget risk analysis</p>
        </div>
        <div class="bg-white rounded-xl p-6 border-2 border-quiltyNavy/10 text-center">
          <p class="text-4xl font-bold text-quiltyRed mb-2">Multi</p>
          <p class="text-sm text-quiltyNavy/70">AI model ensemble</p>
        </div>
        <div class="bg-white rounded-xl p-6 border-2 border-quiltyNavy/10 text-center">
          <p class="text-4xl font-bold text-quiltyTeal mb-2">14K+</p>
          <p class="text-sm text-quiltyNavy/70">Industry contacts</p>
        </div>
        <div class="bg-white rounded-xl p-6 border-2 border-quiltyNavy/10 text-center">
          <p class="text-4xl font-bold text-quiltyRed mb-2">&lt;5min</p>
          <p class="text-sm text-quiltyNavy/70">From script to strategy</p>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA Section -->
  <section class="py-16 px-4 sm:px-6 lg:px-8 bg-quiltyNavy dark">
    <div class="container mx-auto max-w-4xl text-center">
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-6 font-serif">
        Ready to Transform Your Development Process?
      </h2>
      <p class="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
        Experience the full power of Hollywood's intelligence infrastructure.
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <Button size="lg" href="/login?mode=register" class="group">
          <Sparkles class="w-4 h-4 mr-2 group-hover:animate-pulse" />
          Get Started
        </Button>
        <Button
          size="lg"
          variant="outline"
          href="mailto:demo@quilty.app"
          class="border-white/20 text-white hover:bg-white/10"
        >
          Schedule Live Demo
        </Button>
      </div>
    </div>
  </section>
</div>

<style>
  /* Ensure proper color classes are applied */
  :global(.text-quiltyTeal) {
    color: #8b9d83;
  }

  :global(.text-quiltyRed) {
    color: #6b5d4f;
  }

  :global(.bg-quiltyTeal\/20) {
    background-color: rgba(139, 157, 131, 0.2);
  }

  :global(.bg-quiltyRed\/20) {
    background-color: rgba(107, 93, 79, 0.2);
  }

  /* Smooth transitions */
  button {
    transition: all 0.3s ease;
  }

  /* Respect prefers-reduced-motion */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
